import _ from 'underscore';
import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from 'react';
import PropTypes from 'prop-types';
import Swiper from 'react-id-swiper';
import cx from 'classnames';
import { capitalize } from '@culture/common-helpers/string';

import Icon from 'components/Icon';

import styles from './styles.module.scss';

const Slider = ({
	getSwiper,
	params,
	theme,
	withNavigation,
	paginationMarkupAttributes,
	children,
	slidesPerView,
	navigationIcon
}) => {
	const swiperRef = useRef(null);
	const [swiper, setSwiper] = useState(null);
	const [noSwiping, setNoSwiping] = useState(false);

	useEffect(() => {
		setNoSwiping(() => {
			if (
				swiper &&
				!swiper.destroyed &&
				swiper.slides &&
				swiper.slides.length &&
				swiper.isBeginning
			) {
				const lastSlide = swiper.slides[swiper.slides.length - 1];
				const { right } = lastSlide.getBoundingClientRect();

				return right < swiper.$el.outerWidth(true);
			}

			return false;
		});
	}, [swiper, children]);

	const renderNavButton = useCallback(
		(type, hideNavigation) => {
			const buttonClassCx = cx(styles.button, styles[type], {
				[styles.hidden]: hideNavigation
			});

			return (
				<div className={buttonClassCx} {...paginationMarkupAttributes}>
					<Icon
						icon={`${navigationIcon}${capitalize(type)}`}
						className={styles.icon}
					/>
				</div>
			);
		},
		[navigationIcon, paginationMarkupAttributes]
	);

	const memoSwiperParams = useMemo(() => {
		const hideNavigation =
			_.isNumber(slidesPerView) && slidesPerView >= children.length;

		const containerClass = cx('swiper-container', {
			'swiper-no-swiping': hideNavigation
		});

		const onInit = () => {
			if (swiperRef && swiperRef.current) {
				const swiperInstance = swiperRef.current.swiper;

				setSwiper(swiperInstance);
				getSwiper?.(swiperInstance);
			}
		};

		let swiperParams = {
			containerClass,
			slidesPerView: 'auto',
			spaceBetween: 14,
			watchOverflow: true,
			updateOnWindowResize: true,
			shouldSwiperUpdate: true,
			observer: true,
			on: { init: onInit },
			...params
		};

		if (withNavigation) {
			swiperParams = {
				...swiperParams,
				renderNextButton: () => renderNavButton('right', hideNavigation),
				renderPrevButton: () => renderNavButton('left', hideNavigation),
				navigation: {
					nextEl: `.${styles.button}.${styles.right}`,
					prevEl: `.${styles.button}.${styles.left}`
				}
			};
		}
		return swiperParams;
	}, [withNavigation, params, renderNavButton]);

	const sliderClass = cx(styles.slider, styles[`theme_${theme}`], {
		[styles.hidden]: !swiper,
		[styles.hideNavigation]: noSwiping,
		'swiper-no-swiping': noSwiping
	});

	return (
		!_.isEmpty(children) && (
			<div className={sliderClass}>
				<Swiper {...memoSwiperParams} ref={swiperRef}>
					{React.Children.map(children, (child) => (
						<div key={child.key}>{child}</div>
					))}
				</Swiper>
			</div>
		)
	);
};

Slider.propTypes = {
	params: PropTypes.object,
	getSwiper: PropTypes.func,
	theme: PropTypes.oneOf([
		'preview',
		'morphing',
		'rows',
		'darkTags',
		'lightTags',
		'mobileEdge'
	]),
	navigationIcon: PropTypes.oneOf(['arrow', 'chevronSmall']),
	withNavigation: PropTypes.bool,
	slidesPerView: PropTypes.number,
	paginationMarkupAttributes: PropTypes.object,
	children: PropTypes.node
};

Slider.defaultProps = {
	navigationIcon: 'arrow',
	withNavigation: false
};

export default Slider;
